import { useEffect, useState } from "react";

export type Theme = "dark" | "light";
export type Density = "comfy" | "compact";
export type Accent = "blue" | "amber" | "emerald" | "rose";

export interface Tweaks {
  theme: Theme;
  density: Density;
  accent: Accent;
  showAgent: boolean;
}

const STORAGE_KEY = "atrium.tweaks";

const DEFAULTS: Tweaks = {
  theme: "dark",
  density: "comfy",
  accent: "amber",
  showAgent: true,
};

function read(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

export const ACCENT_HEX: Record<Accent, string> = {
  blue: "#7aa2ff",
  amber: "#fbbf24",
  emerald: "#34d399",
  rose: "#fb7185",
};

export function useTweaks(): [Tweaks, <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void] {
  const [tweaks, setTweaks] = useState<Tweaks>(() => read());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
    } catch {
      /* no-op */
    }
  }, [tweaks]);

  function setTweak<K extends keyof Tweaks>(k: K, v: Tweaks[K]) {
    setTweaks((t) => ({ ...t, [k]: v }));
  }

  return [tweaks, setTweak];
}
